import { useState } from 'react';
import { ModerationDecisionButtons } from '@/features/moderation-actions';
import {
  pendingSlotsOf,
  requestEntityName,
  requestOperation,
  resolveEntityName,
  sourceLabel,
  ModerationOperationPill,
  type ModerationEntityType,
  type ModifierGroupCard,
  type PendingSlotVersion,
  type Translate,
} from '@/entities/moderation';
import { fieldLabel } from '../../lib/field-labels';
import { ModifiersSection } from './modifiers-section';
import { ReviewGate } from './review-gate';
import { ReviewSection } from './review-modal-shell';

/**
 * The dish's modifier groups that wait on a request of their OWN
 * (`v1ProductModerationCard.modifier_groups`). There is no modifier-group
 * queue in this panel, so each pending slot is decided right here — live
 * group beside the proposal, with its own reject / approve pair. Groups
 * riding inside the dish's own request (`payload.companions`) are not
 * listed: they are decided with the dish.
 */
export function PendingModifierGroupsSection({
  entityType,
  groups,
  t,
}: {
  entityType: ModerationEntityType;
  groups: ModifierGroupCard[];
  t: Translate;
}) {
  const pending = groups.flatMap((group) =>
    pendingSlotsOf(group).map((slot) => ({ group, slot })),
  );
  if (pending.length === 0) return null;

  return (
    <ReviewSection
      title={t('review.modifierGroupsPending', 'Группы модификаторов на модерации')}
      divider={false}
    >
      <p className="text-sm text-muted-foreground">
        {t(
          'review.modifierGroupsHint',
          'У этих групп своя заявка — решение по ним принимается отдельно от товара.',
        )}
      </p>
      <ul className="flex flex-col gap-3" data-testid="moderation-modifier-groups">
        {pending.map(({ group, slot }) => (
          <PendingGroupRow
            key={slot.version.change_request?.id ?? `${slot.slot}-${groupKey(group)}`}
            entityType={entityType}
            group={group}
            slot={slot}
            t={t}
          />
        ))}
      </ul>
    </ReviewSection>
  );
}

function groupKey(group: ModifierGroupCard): string {
  return (
    group.prod?.entity?.id ?? group.manual?.entity?.id ?? group.sync?.entity?.id ?? ''
  );
}

function PendingGroupRow({
  entityType,
  group,
  slot,
  t,
}: {
  entityType: ModerationEntityType;
  group: ModifierGroupCard;
  slot: PendingSlotVersion;
  t: Translate;
}) {
  const [reviewed, setReviewed] = useState(false);
  const request = slot.version.change_request;
  const proposed = slot.version.entity ?? {};
  const live = group.prod?.entity ?? null;
  const name = resolveEntityName(
    proposed.name ?? live?.name,
    request ? requestEntityName(request, '—') : '—',
  );
  const changed = slot.version.changed_fields;

  return (
    <li className="flex flex-col gap-3 rounded-[20px] border border-border p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 text-sm font-semibold break-words text-foreground">
          {name}
        </p>
        {request && (
          <ModerationOperationPill operation={requestOperation(request)} t={t} />
        )}
        <span className="text-xs text-muted-foreground">{sourceLabel(slot.slot, t)}</span>
      </div>
      {changed.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {t('review.changedFieldsList', 'Изменено: {{fields}}').replace(
            '{{fields}}',
            changed.map((key) => fieldLabel(key, t)).join(', '),
          )}
        </p>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <GroupColumn
          title={t('detail.currentVersion', 'Текущая версия')}
          entity={live}
          other={null}
          t={t}
          empty={t('review.noLiveGroup', 'Новая группа — текущей версии нет.')}
        />
        <GroupColumn
          title={t('detail.newVersion', 'Новая версия')}
          entity={proposed}
          other={live}
          t={t}
          empty={t('detail.noPendingVersion', 'Нет предложенных данных.')}
        />
      </div>
      {request?.id ? (
        <>
          <ReviewGate confirmed={reviewed} onChange={setReviewed} />
          <ModerationDecisionButtons
            entityType={entityType}
            changeRequestId={request.id}
            reviewConfirmed={reviewed}
            approveLabel={t('create.approve', 'Одобрить')}
            rejectLabel={t('create.decline', 'Отклонить')}
          />
        </>
      ) : null}
    </li>
  );
}

function GroupColumn({
  title,
  entity,
  other,
  t,
  empty,
}: {
  title: string;
  entity: Record<string, unknown> | null;
  other: Record<string, unknown> | null;
  t: Translate;
  empty: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-[16px] bg-muted/50 p-3">
      <p className="text-xs font-medium text-muted-foreground">{title}</p>
      {entity && Object.keys(entity).length > 0 ? (
        // A modifier group renders through the product's modifiers block —
        // wrapped as a one-group dish so both share one layout.
        <ModifiersSection
          own={{ modifiers: [entity] }}
          other={other ? { modifiers: [other] } : null}
          highlight={other !== null}
          t={t}
        />
      ) : (
        <p className="text-sm text-muted-foreground">{empty}</p>
      )}
    </div>
  );
}
